import { useMemo } from 'react';
import { ReactNode } from 'react';

import { Link, useParams } from 'react-router-dom';

import { RunStatus, Interval } from '../../../../../../../backend/src/types/types';
import { useSubmitRunApproval } from '@/api/mutations/useSubmitRunApproval';
import { useGetCase } from '@/api/queries/useGetCase';
import { Run } from '@/api/types/backend-dto-types';
import { PipelineProcessingStageDescriptions, ProcessingStatus, isProcessingStatus } from '@/constants/pipelineStatus';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { cn } from '@/new/lib/utils';

export function formatDurationEstimate(durationEstimate?: Interval | null): string {
  if (!durationEstimate) return '';
  const parts: string[] = [];
  const days = durationEstimate?.days ?? 0;
  const hours = durationEstimate?.hours ?? 0;
  const minutes = durationEstimate?.minutes ?? 0;
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  return parts.join(' ');
}

export function RunInfoPopover({
  trigger,
  title,
  credits,
  durationEstimate,
  stage,
  actions,
}: {
  trigger: ReactNode;
  title: string;
  credits: number;
  durationEstimate?: Interval | null;
  stage?: ProcessingStatus;
  actions?: ReactNode;
}) {
  const durationText = formatDurationEstimate(durationEstimate);
  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align={'end'} className="w-80 p-3">
        <div className="mb-2 flex items-center gap-2 border-b pb-2">
          <Icon className="lucide--file-cog text-gray-700" />
          <h4 className="font-serif text-sm font-semibold text-gray-900">{title}</h4>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-2 rounded-md border border-dashed border-blue-200 bg-blue-50 px-2.5 py-1">
            <Icon className="lucide--file text-xs text-blue-600" />
            <div>
              <p className="text-xs font-semibold text-gray-900">{credits.toLocaleString?.() ?? credits}</p>
              <p className="text-[10px] text-gray-500">Credits</p>
            </div>
          </div>
          {durationText && (
            <div className="flex items-center gap-2 rounded-md border border-dashed border-purple-200 bg-purple-50 px-2.5 py-1">
              <Icon className="lucide--file-clock text-xs text-purple-600" />
              <div>
                <p className="text-xs font-semibold text-gray-900">{durationText}</p>
                <p className="text-[10px] text-gray-500">Estimated Time</p>
              </div>
            </div>
          )}
          {stage && (
            <div className="flex items-center gap-2 rounded-md border border-dashed border-amber-200 bg-amber-50 px-2.5 py-1">
              <div className="text-xs text-gray-900">
                Stage: <strong>{PipelineProcessingStageDescriptions[stage].title}</strong>
              </div>
            </div>
          )}
        </div>
        {actions ? <div className="flex gap-2">{actions}</div> : null}
      </PopoverContent>
    </Popover>
  );
}

const RunProcessing = ({ run }: { run: Run }) => {
  const credits = run?.totalPages ?? 0;
  return (
    <RunInfoPopover
      title="Run in progress"
      credits={credits}
      durationEstimate={run?.durationEstimate}
      stage={run?.status as ProcessingStatus}
      trigger={
        <Button
          size="sm"
          variant="outline"
          className="flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 hover:bg-blue-100"
        >
          <Icon className="lucide--loader-2 mt-0.5 animate-spin text-sm text-blue-600 duration-700" />
          <p className="text-xs font-medium text-blue-900">Documents processing</p>
        </Button>
      }
    />
  );
};

const AwaitingApproval = ({
  awaitingApprovalRun,
  handleApprove,
  handleDeny,
}: {
  awaitingApprovalRun: Run;
  handleApprove: () => void;
  handleDeny: () => void;
}) => {
  const credits =
    (awaitingApprovalRun?.totalPages as number | undefined) ??
    (awaitingApprovalRun.successfulPagesCount || 0) + (awaitingApprovalRun.failedPagesCount || 0);
  return (
    <RunInfoPopover
      title="Run Approval"
      credits={credits}
      durationEstimate={awaitingApprovalRun?.durationEstimate}
      trigger={
        <Button
          size="sm"
          variant="outline"
          className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 hover:bg-amber-100 hover:text-amber-800"
        >
          <Icon className="lucide--shield-alert text-sm text-amber-600" />
          Approval required
        </Button>
      }
      actions={
        <>
          <Button size="sm" variant="primary" className="w-full" onClick={handleApprove}>
            <Icon className="lucide--check" />
            Approve
          </Button>
          <Button size="sm" variant="destructive" className="w-full" onClick={handleDeny}>
            <Icon className="lucide--x" />
            Cancel
          </Button>
        </>
      }
    />
  );
};

const FailedPages = ({
  matterId,
  allDocumentsSubsetId,
  failedPagesCount,
}: {
  matterId: string;
  allDocumentsSubsetId: string;
  failedPagesCount: number;
}) => {
  return (
    <Button asChild variant="destructive">
      <Link
        to={`/matters/${matterId}/subsets/${allDocumentsSubsetId}/docs`}
        className="flex items-center gap-1.5 text-xs"
      >
        <Icon className="lucide--x-circle text-sm text-red-600" />
        {failedPagesCount.toLocaleString()} {failedPagesCount === 1 ? 'page' : 'pages'} not processed
        <Icon className="lucide--chevron-right text-sm text-red-700" />
      </Link>
    </Button>
  );
};

const AllProcessed = () => {
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-2.5 py-1 text-xs text-green-800">
      <Icon className="lucide--check-circle text-sm text-green-600" />
      All processed
    </div>
  );
};

export function DocProcessingInfo({ allDocumentsSubsetId }: { allDocumentsSubsetId: string | undefined }) {
  const { matterId } = useParams();
  if (!matterId || !allDocumentsSubsetId) return;

  const { mutateAsync: submitRunApproval } = useSubmitRunApproval(matterId);
  const { data: caseData } = useGetCase(matterId);
  const runs = caseData?.runs ?? [];

  const processingRuns = runs.filter((run: Run) => isProcessingStatus(run.status as RunStatus)) ?? [];
  const mostRecentRun = useMemo(() => {
    return [...runs].sort(
      (a, b) =>
        new Date(b.createdDate as unknown as string).getTime() - new Date(a.createdDate as unknown as string).getTime(),
    )[0];
  }, [runs]);

  const failedPagesCount = runs.reduce((sum: number, run: Run) => sum + (run.failedPagesCount || 0), 0) ?? 0;

  const handleApprove = async () => {
    if (!mostRecentRun) return;
    await submitRunApproval({ runId: mostRecentRun.id, approvalGranted: true });
  };

  const handleDeny = async () => {
    if (!mostRecentRun) return;
    await submitRunApproval({ runId: mostRecentRun.id, approvalGranted: false });
  };

  const isRunAwaitingApproval = mostRecentRun?.status === RunStatus.AwaitingApproval;
  const isRunProcessing = processingRuns.length > 0;
  const matterHasFailedPages = failedPagesCount > 0;

  return (
    <div
      className={cn(
        `flex items-center rounded-md border border-l-4 border-gray-200 bg-gradient-to-b from-gray-50 to-gray-100 px-3 py-2 lg:col-span-2`,
        isRunAwaitingApproval
          ? 'border-l-amber-300'
          : isRunProcessing
            ? 'border-l-blue-300'
            : matterHasFailedPages
              ? 'border-l-red-300'
              : 'border-l-green-300',
      )}
    >
      <div className="flex w-full flex-col items-center gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex h-full flex-col justify-between">
          <div className="flex items-center gap-2">
            <Icon className="lucide--file-clock text-sm text-gray-800" />
            <h3 className="text-xs font-medium text-gray-900">Document Processing</h3>
          </div>
          <p className="text-xs text-gray-700">View document processing status.</p>
        </div>

        <div className="space-y-2 sm:flex sm:flex-wrap sm:items-center sm:gap-2 sm:space-y-0">
          {isRunAwaitingApproval ? (
            <AwaitingApproval
              awaitingApprovalRun={mostRecentRun}
              handleApprove={handleApprove}
              handleDeny={handleDeny}
            />
          ) : isRunProcessing ? (
            <RunProcessing run={mostRecentRun} />
          ) : matterHasFailedPages ? (
            <FailedPages
              matterId={matterId}
              allDocumentsSubsetId={allDocumentsSubsetId}
              failedPagesCount={failedPagesCount}
            />
          ) : (
            <AllProcessed />
          )}
        </div>
      </div>
    </div>
  );
}
